import { Check } from 'lucide-react'
import { cn } from 'utils/cn'
import { AutocompleteRow } from './Autocomplete.utils'

interface AutocompleteOptionsListProps {
    rows: AutocompleteRow[]
    filteredCount: number
    emptyMessage: string
    selectedValues: string[]
    panelId: string
    onSelect: (value: string) => void
    // A sticky summary line sits above the list, so sticky group headers dock beneath it (its height is `h-6`).
    hasSummary?: boolean
    // Keyboard highlight only applies to single-select — the legacy `multiple` popover never sets a
    // highlighted index, so this stays off there.
    highlightEnabled: boolean
    highlightedIndex: number
    onHighlight: (index: number) => void
}

// Extracted from Autocomplete so the row-building logic (buildRows, in Autocomplete.utils) and the
// row-rendering logic each have their own home — this piece owns turning `AutocompleteRow[]` into
// DOM: a header renders as a non-interactive, aria-hidden label; an option renders as a real button
// with a stable id (`${panelId}-option-${value}`) that `aria-activedescendant` targets.
export const AutocompleteOptionsList = ({
    rows,
    filteredCount,
    emptyMessage,
    selectedValues,
    panelId,
    onSelect,
    hasSummary = false,
    highlightEnabled,
    highlightedIndex,
    onHighlight,
}: AutocompleteOptionsListProps) => {
    if (filteredCount === 0) {
        return <div className="px-3 py-1.5 text-sm font-medium leading-[1.43] text-foreground opacity-50">{emptyMessage}</div>
    }

    let optionIndex = -1
    return (
        <>
            {rows.map((row) => {
                if (row.type === 'header') {
                    // Not a button, and marked aria-hidden: never focusable, never reachable by
                    // arrow-key nav (it's excluded from the option index below), never clickable,
                    // never announced as a list item — a header is a label, not an option.
                    return (
                        <div
                            key={`group-${row.key}`}
                            aria-hidden="true"
                            // A header is a non-focusable div: without this, its mousedown blurs the
                            // trigger input (relatedTarget null) and the panel reverts/closes before the
                            // click that would have landed on it — same reason the chevron/clear buttons
                            // carry this guard.
                            onMouseDown={(e) => e.preventDefault()}
                            className={cn(
                                // Sticky: the current group's header stays pinned while its options scroll, until the next header pushes it out.
                                'sticky z-10 shrink-0 bg-surface px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-foreground-muted first:pt-1',
                                hasSummary ? 'top-6' : 'top-0'
                            )}
                        >
                            {row.label}
                        </div>
                    )
                }
                if (!row.option.disabled) optionIndex += 1
                const index = optionIndex
                const opt = row.option
                const isSelected = selectedValues.includes(opt.value)
                const isHighlighted = !opt.disabled && highlightEnabled && index === highlightedIndex
                return (
                    <button
                        key={opt.value}
                        id={`${panelId}-option-${opt.value}`}
                        type="button"
                        disabled={opt.disabled}
                        title={opt.title ?? (typeof opt.label === 'string' ? opt.label : undefined)}
                        aria-disabled={opt.disabled || undefined}
                        onClick={() => onSelect(opt.value)}
                        // Chrome/Firefox focus a <button> on click, so the panel's own blur guard
                        // (`panelRef.current?.contains(relatedTarget)`) sees it land inside the panel and
                        // leaves the panel open for the click that follows. Safari on macOS does NOT focus
                        // a button on click — relatedTarget comes back null there, same as an unguarded
                        // header — so without preventDefault the panel unmounts on mousedown and the click
                        // this button was about to receive never arrives.
                        onMouseDown={(e) => e.preventDefault()}
                        // One highlight, not two. Where the keys work the pointer MOVES the highlight instead of painting
                        // its own CSS hover, which stayed on the row under a resting mouse while the arrows lit another.
                        // On move, not enter: a list scrolled by the arrow keys slides rows under a still pointer.
                        onMouseMove={() => !opt.disabled && highlightEnabled && index !== highlightedIndex && onHighlight(index)}
                        className={cn(
                            'flex min-h-9 w-full shrink-0 items-center gap-3 rounded-[20px] px-3 py-1.5 text-left text-sm font-medium leading-[1.43] text-foreground transition-colors duration-150',
                            !highlightEnabled && 'hover:bg-default',
                            isHighlighted && 'bg-default',
                            opt.disabled && 'cursor-not-allowed'
                        )}
                    >
                        <span className={cn('min-w-0 flex-1 break-words', opt.disabled && 'opacity-50')}>{opt.label}</span>
                        {opt.trailingContent}
                        {isSelected && (
                            <span className="flex shrink-0 items-center text-foreground">
                                <Check size={16} strokeWidth={1.5} />
                            </span>
                        )}
                    </button>
                )
            })}
        </>
    )
}
